<template>
  <div class="kpi-container" >
    <KpiHeader  title="My KPI-Overview"
                subtitle="An overview of my most important key performance indicators"
                :view-mode="'grid'"
                @view-changed="onKpiHeaderViewChanged"
                @config-click="showConfigDialog = true"
                @add-click="onKpiHeaderAddKpi" />

      <KpiGrid :columns="viewMode === 'grid' ? 3 : 1">
        <template v-for="(kpi, index) in kpiData" :key="kpi.id">

          <KpiCardAlternative v-if="sidebarTheme === 'skewed'"
                              :kpi="kpi"
                              :score="90"
                              :target="96"
                              :previous-wave-score="80"
                              scenario="Scenario B"
                              wave="25T3"
                              status="Beendet"
                              country="DE" />

          <KpiCard  v-else
                    :kpi="kpi"
                    @click-kpi="onKpiCardClick"
                    @edit-kpi="onKpiCardEdit"
                    @delete-kpi="onKpiCardDelete" />
      </template>



      <div  v-if="viewMode === 'grid' && kpiData.length % 3 !== 0"
            class="kpi-add-placeholder"
            :class="{ 'kpi-add-placeholder-skew': sidebarTheme === 'skewed' }" @click="onKpiHeaderAddKpi">
        <div class="placeholder-content">
          <Plus :size="32" class="placeholder-icon" />
          <p class="placeholder-text">{{ $t('Webportal.Kpi.Add')}}</p>
        </div>
      </div>
    </KpiGrid>

    <!-- Zentraler Edit Dialog -->
    <KpiEditDialog  v-model="showEditDialog"
                    :kpi="selectedKpi"
                    @kpi-edit-save="onKpiEditSave" />

    <GridConfigDialog v-model="showConfigDialog"
                      :initialConfig="gridConfig"
                      @grid-config-save="onGridConfigSave" />

    <!-- Optional: Delete Confirmation Dialog -->
    <b-modal  v-model="showDeleteDialog"
              title="KPI-Instanz löschen"
              ok-variant="danger"
              ok-title="Löschen"
              cancel-title="Abbrechen"
              @ok="onConfirmDelete">
      <p>Möchten Sie die KPI-Instanz "{{ selectedKpi?.name }}" wirklich löschen?</p>
    </b-modal>

    <!-- Optional: Add KPI Dialog -->
    <KpiAddDialog v-if="showAddDialog"
                  v-model="showAddDialog"
                  :kpi="selectedKpi"
                  @kpis-added="handleKpisAdded"
                  @kpi-add-save="handleKpisAdded"
                  @kpi-add-cancel="handleAddCancel" />
  </div>
</template>

<script>
import KpiHeader from './KpiHeader.vue';
import KpiGrid from './KpiGrid.vue';
import KpiCard from './KpiCard.vue';
import GridConfigDialog from './GridConfigDialog.vue';
import KpiEditDialog from './KpiEditDialog.vue';
import KpiAddDialog from './KpiAddDialog.vue';
import { Plus } from 'lucide-vue-next';
import { useToast } from 'bootstrap-vue-next';
import { mapGetters } from 'vuex';
import { useKpiHelpers } from '@/composables/kpi-helpers';
import KpiCardAlternative from '@/componentsv3/KpiBoard/KpiCardAlternative.vue';
export default {
  name: 'KpiBoard',

  components: {
    KpiHeader,
    KpiGrid,
    KpiCard,
    KpiCardAlternative,
    GridConfigDialog,
    KpiEditDialog,
    KpiAddDialog,
    Plus
  },

  props: {
    boardData:      { type: Object, required: true },
    boardSetup:     { type: Object, required: true },
    kpis:           { type: Array, required: true }
  },

  setup() {
    const { show } = useToast();
    const kpiHelpers = useKpiHelpers();
    return { showToast: show, kpiHelpers };
  },

  data() {
    return {
      gridConfig: {
        sortBy: 'name',
        sortOrder: 'asc',
        clickToDetails: true
      },
      kpiData: this.getKpis(this.boardData),
      selectedKpi: null,
      showAddDialog: false,
      showConfigDialog: false,
      showEditDialog: false,
      showDeleteDialog: false,
      viewMode: 'grid'
    };
  },

  watch: {
    boardData(newVal) {
      this.kpiData = this.getKpis(newVal);
    }
  },

  computed: {
    ...mapGetters('app', [ 'sidebarTheme' ]),

    kpiSubtitle() {
      const count = this.kpiData.length;
      return `${count} KPI-Instanzen konfiguriert`;
    },

    sortedKpis() {
      const sorted = [...this.kpiData];
      const { sortBy, sortOrder } = this.gridConfig;

      sorted.sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];

        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      return sorted;
    },
  },

  methods: {
    getKpis(boardData){
      if (!boardData || Object.keys(boardData).length === 0) return [];
      let items = [];
      boardData.Cluster?.splice(0).forEach(c => items.push({ type: 'cluster', ... c }));
      boardData.KPI?.forEach(k => items.push({ type: 'kpi', ... k }));
      boardData.KPIGroup?.forEach(g => items.push({ type: 'kpigroup', ... g }));
      let kpis = [];
      log.warn("Controls ", items, this.boardSetup.controls);
      for (let control of items){

        if (control.type === 'kpi' && boardData.KPI) {
          const kpiInstance = boardData.KPI.find(k => k.Code === control.kpiCode);
          if (!kpiInstance) {
            log.warn("KPI not found for code: ", control.kpiCode);
          }
          kpis.push({ ...control, ...kpiInstance});
        } else if (control.type === 'kpigroup' && boardData.KPIGroup) {
          const kpiGroup = boardData.KPIGroup.find(k => k.Code === control.kpiCode || k.Code.includes(control.kpiCode));
          kpis.push({ ... control, ... kpiGroup ? kpiGroup : boardData.KPIGroup[0] });
        } else if (control.type === 'cluster' && boardData.Cluster) {
          const cluster = boardData.Cluster.find(k => k.Code === control.Code || k.Code.includes(control.Code));
          if (!cluster) {
            log.warn("Cluster not found for code: ", control.kpiCode);
          }
          kpis.push({ ... control, ... cluster});
        } else if (control.type === 'question' && boardData.Question ) {
          const question = boardData.Question.find(k => k.Code === control.kpiCode || k.Code.includes(control.kpiCode));
          if (!question) {
            log.warn("Question not found for code: ", control.kpiCode);
          }
          kpis.push({ ... control, ... question});
        } else {
          log.warn(`${control.type} not found for code: `, control.kpiCode);
        }
      }
      return kpis; // [boardData.KPIGroup[2], boardData.KPIGroup[3]];
    },

    onKpiHeaderViewChanged(newView) {
      this.viewMode = newView;
    },

    onGridConfigSave(config) {
      this.gridConfig = { ...config };

      this.showToast({
        props: {
          title: this.$t("Webportal.ConfigurationSaved"),
          variant: "success",
          autoHide: true,
          delay: 3000,
        }
      });
    },

    onKpiCardClick(kpi) {
      if (this.gridConfig.clickToDetails) {
        // NotImplemented: Navigate to KPI detail view
        // this.$router.push({ name: 'KpiDetail', params: { id: kpi.id } });
      }
    },

    onKpiCardEdit(kpi) {
      this.selectedKpi = kpi;
      this.showEditDialog = true;
    },

    onKpiCardDelete(kpi) {
      this.selectedKpi = kpi;
      this.showDeleteDialog = true;
    },

    onKpiEditSave(updatedKpi) {
      const index = this.kpiData.findIndex(k => k.id === updatedKpi.id);
      if (index !== -1) {
        this.kpiData.splice(index, 1, updatedKpi);
      }

      this.showToast({
        props: {
          title: this.$t("Webportal.KpiUpdated", {
            entity: 'Saved',
          }),
          variant: "success",
          autoHide: true,
          delay: 3000,
        }
      });
    },

    onKpiHeaderAddKpi() {
      this.showAddDialog = true;
    },

    handleKpisAdded(newKpis) {
      this.kpiData.push(...newKpis);

      this.showToast({
        props: {
          title: this.$t("Webportal.KpisAdded", {
            count: newKpis.length,
          }),
          variant: "success",
          autoHide: true,
          delay: 3000,
        }
      });
    },

    onConfirmDelete() {
      const index = this.kpiData.findIndex(k => k.id === this.selectedKpi.id);
      if (index !== -1) {
        this.kpiData.splice(index, 1);
      }

      this.showToast({
        props: {
          title: this.$t("Webportal.KpiDeleted", {
            entity: 'Deleted',
          }),
          variant: "success",
          autoHide: true,
          delay: 3000,
        }
      });

      this.selectedKpi = null;
    },

    handleAddCancel() {
      this.showAddDialog = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.kpi-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}
.kpi-add-placeholder-skew {
  border: 1px solid var(--color-border);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transform: skewX(var(--target-skew-angle-inverse));
  position: relative;

  > * {
    transform: skewX(var(--target-skew-angle));
  }

}

.kpi-add-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  border: 2px dashed var(--bs-border-color);
  border-radius: 0.75rem;
  background-color: var(--bs-body-bg);
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    border-color: var(--bs-primary);
    // background-color: var(--color-accent);

    .placeholder-icon {
      color: var(--target-primary);
      transform: scale(1.1);
    }

    .placeholder-text {
      color: var(--target-primary);
    }
  }

  &:active {
    transform: scale(0.98);
  }
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
}

.placeholder-icon {
  color: var(--bs-secondary-color);
  transition: all 0.15s ease-in-out;
}

.placeholder-text {
  font-size: 0.875rem;
  color: var(--bs-secondary-color);
  margin: 0;
  transition: color 0.15s ease-in-out;
}
</style>
